import { ApiError, createAction } from 'redux-api-middleware';
import { State as StoreState } from '../types/State';
import {
  HTTP_CONTENT_TYPE_HEADER,
  HTTP_CONTENT_TYPE_JSON,
  HTTP_GET,
  HTTP_PATCH,
  HTTP_POST,
} from './ApiAction';

export const LOAD_PROFILE = 'barmer-bonus-shared/profile/LOAD' as const;
export const LOAD_PROFILE_SUCCESS = 'barmer-bonus-shared/profile/SUCCESS' as const;
export const LOAD_PROFILE_FAILURE = 'barmer-bonus-shared/profile/FAILURE' as const;

export const ADD_ADDITIONAL_INSURED = 'barmer-bonus-shared/profile/ADD_ADDITIONAL_INSURED' as const;
export const ADD_ADDITIONAL_INSURED_SUCCESS = 'barmer-bonus-shared/profile/ADD_ADDITIONAL_INSURED_SUCCESS' as const;
export const ADD_ADDITIONAL_INSURED_FAILURE = 'barmer-bonus-shared/profile/ADD_ADDITIONAL_INSURED_FAILURE' as const;
export const ADD_ADDITIONAL_INSURED_RESET = 'barmer-bonus-shared/profile/ADD_ADDITIONAL_INSURED_RESET' as const;

export const ADDITIONAL_INSURED_LOAD_STATE_IDLE = 'idle' as const;
export const ADDITIONAL_INSURED_LOAD_STATE_LOADING = 'loading' as const;
export const ADDITIONAL_INSURED_LOAD_STATE_SUCCESS = 'success' as const;
export const ADDITIONAL_INSURED_LOAD_STATE_FAILURE = 'failure' as const;

export const UPDATE_ANALYTICS = 'barmer-bonus-shared/update-analytics/LOAD' as const;
export const UPDATE_ANALYTICS_SUCCESS = 'barmer-bonus-shared/update-analytics/SUCCESS' as const;
export const UPDATE_ANALYTICS_FAILURE = 'barmer-bonus-shared/update-analytics/FAILURE' as const;

type LoadProfile = { type: typeof LOAD_PROFILE; payload?: ApiError; error?: boolean };

type SuccessProfile = {
  type: typeof LOAD_PROFILE_SUCCESS;
  payload: Profile;
};

type FailureProfile = { type: typeof LOAD_PROFILE_FAILURE; payload: ApiError; error: boolean };

type AddAdditionalInsured = {
  type: typeof ADD_ADDITIONAL_INSURED;
  payload?: ApiError;
  error?: boolean;
};
type AddAdditionalInsuredSuccess = {
  type: typeof ADD_ADDITIONAL_INSURED_SUCCESS;
  payload?: ApiError;
  error?: boolean;
};
type AddAdditionalInsuredFailure = {
  type: typeof ADD_ADDITIONAL_INSURED_FAILURE;
  payload?: ApiError;
  error?: boolean;
};
type AddAdditionalInsuredReset = { type: typeof ADD_ADDITIONAL_INSURED_RESET; payload: {} };

type UpdateAnalytics = {
  type: typeof UPDATE_ANALYTICS;
};

type UpdateAnalyticsSuccess = {
  type: typeof UPDATE_ANALYTICS_SUCCESS;
  payload: AnalyticsPatchPayload;
};

type UpdateAnalyticsFailure = {
  type: typeof UPDATE_ANALYTICS_FAILURE;
  payload?: ApiError;
  error?: boolean;
};

export type Action =
  | LoadProfile
  | SuccessProfile
  | FailureProfile
  | AddAdditionalInsured
  | AddAdditionalInsuredSuccess
  | AddAdditionalInsuredFailure
  | AddAdditionalInsuredReset
  | UpdateAnalytics
  | UpdateAnalyticsSuccess
  | UpdateAnalyticsFailure;

export type Membership = {
  loyaltyProgramName: string;
  isRegistered: boolean;
  cancellationDate: string | null | undefined; // "2018-05-31T13:17:40.352Z", // or null
  email: string | null | undefined;
  endDate: string | null | undefined; // "2018-05-31T13:17:40.352Z, // or null
  loyaltyProgramId: number;
  membershipId: number;
  registrationDate: string; // "2018-05-31T13:17:40.352Z",
  startDate: string; // "2018-05-31T13:17:40.352Z",
  status: string;
};

export type SubProfile = {
  id: string;
  birthday: string; // "2018-05-31T13:17:40.352Z",
  city: string;
  countryCode: string;
  firstname: string;
  kvnr: string;
  lastname: string;
  memberships: Array<Membership>;
  namePrefix: string;
  participantId: number;
  salutation: string;
  street: string;
  title: string;
  zip: string;
};

export type Profile = {
  birthday: string; // "2018-05-31T13:17:40.352Z",
  city: string;
  countryCode: string;
  firstname: string;
  kvnr: string;
  lastname: string;
  memberships: Array<Membership>;
  analyticsEnabled?: boolean | null;
  errorMonitoringEnabled?: boolean | null;
  namePrefix: string;
  participantId: number;
  salutation: string;
  street: string;
  title: string;
  zip: string;
  additionalInsured: Array<SubProfile>;
};

type SubProfileLoadingState =
  | typeof ADDITIONAL_INSURED_LOAD_STATE_IDLE
  | typeof ADDITIONAL_INSURED_LOAD_STATE_LOADING
  | typeof ADDITIONAL_INSURED_LOAD_STATE_SUCCESS
  | typeof ADDITIONAL_INSURED_LOAD_STATE_FAILURE;

export type State = {
  loading: boolean;
  addSubProfileState: SubProfileLoadingState;
  profile: Profile | null | undefined;
  error?: ApiError;
};

const initialState: State = {
  loading: false,
  profile: null,
  error: undefined,
  addSubProfileState: ADDITIONAL_INSURED_LOAD_STATE_IDLE,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_PROFILE:
      return {
        ...state,
        loading: action.error === undefined,
        error: action.error ? action.payload : undefined,
      };
    case LOAD_PROFILE_SUCCESS:
      return {
        ...state,
        loading: false,
        profile: action.payload,
        error: undefined,
      };
    case LOAD_PROFILE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case ADD_ADDITIONAL_INSURED:
      return {
        ...state,
        addSubProfileState: ADDITIONAL_INSURED_LOAD_STATE_LOADING,
      };
    case ADD_ADDITIONAL_INSURED_SUCCESS:
      return {
        ...state,
        addSubProfileState: ADDITIONAL_INSURED_LOAD_STATE_SUCCESS,
        error: undefined,
      };
    case ADD_ADDITIONAL_INSURED_FAILURE:
      return {
        ...state,
        addSubProfileState: ADDITIONAL_INSURED_LOAD_STATE_FAILURE,
        error: action.error ? action.payload : undefined,
      };
    case ADD_ADDITIONAL_INSURED_RESET:
      return {
        ...state,
        addSubProfileState: ADDITIONAL_INSURED_LOAD_STATE_IDLE,
        error: undefined,
      };
    case UPDATE_ANALYTICS:
      return {
        ...state,
        loading: true,
      };
    case UPDATE_ANALYTICS_SUCCESS:
      if (!state.profile) {
        return {
          ...state,
          loading: false,
        };
      }

      return {
        ...state,
        loading: false,
        profile: {
          ...state.profile,
          ...action.payload,
        },
      };
    case UPDATE_ANALYTICS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error ? action.payload : undefined,
      };
    default:
      return state;
  }
}

// Whether the profile has been loaded
export function isLoaded(globalState: StoreState): boolean {
  return globalState.profile.profile != null;
}

// Whether the profile is being loaded
export function isLoading(globalState: StoreState): boolean {
  return globalState.profile.loading;
}

export function userHasGermanAddress(globalState: StoreState): boolean {
  if (globalState.profile.profile != null) {
    if (globalState.dashboard.activeUserId === '') {
      return globalState.profile.profile.countryCode === 'D';
    }
    const activeUser:
      | SubProfile
      | null
      | undefined = globalState.profile.profile.additionalInsured.find(
      (insured) => insured.id === globalState.dashboard.activeUserId,
    );
    return activeUser != null && activeUser.countryCode === 'D';
  }
  return false;
}

/**
 * Load profile
 *
 * @param refresh true when we want to always load from BE
 */
export const loadProfile = (refresh = false) =>
  createAction({
    endpoint: '/profile',
    types: [LOAD_PROFILE, LOAD_PROFILE_SUCCESS, LOAD_PROFILE_FAILURE],
    method: HTTP_GET,
    bailout: (state: StoreState) => {
      return (!refresh && isLoaded(state)) || isLoading(state);
    },
  });

/**
 * Request to add a sub profile (additional insured)
 */
export const addAdditionalInsured = (kvnr: string) =>
  createAction({
    endpoint: '/bonus/register-additional',
    types: [ADD_ADDITIONAL_INSURED, ADD_ADDITIONAL_INSURED_SUCCESS, ADD_ADDITIONAL_INSURED_FAILURE],
    method: HTTP_POST,
    headers: { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON },
    body: JSON.stringify({ kvnr }),
  });

/**
 * Reset loading state
 */
export function resetAdditionalInsuredLoadingState(): Action {
  return { type: ADD_ADDITIONAL_INSURED_RESET, payload: {} };
}

type AnalyticsPatchPayload = {
  analyticsEnabled?: boolean;
  errorMonitoringEnabled?: boolean;
};

export const updateAnalyticsAction = (analytics: AnalyticsPatchPayload) =>
  createAction({
    endpoint: '/profile',
    types: [
      UPDATE_ANALYTICS,
      {
        type: UPDATE_ANALYTICS_SUCCESS,
        payload: analytics,
      },
      UPDATE_ANALYTICS_FAILURE,
    ],
    method: HTTP_PATCH,
    headers: { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON },
    body: JSON.stringify(analytics),
  });
